// No dependencies, catalogue download, storage or Admin API access.
const SIZE = 'filter.p.m.custom.custom_size';
const GENDER = 'filter.p.m.custom.gender_targets';
const TYPE = 'filter.p.m.custom.produkttyp';
const HELPER = 'orikiddo-recommendation-candidates';
const normalize = (value) => String(value ?? '').normalize('NFC').trim().toLocaleLowerCase('de');
const values = (list) => Array.isArray(list) ? [...new Set(list.map(normalize).filter(Boolean))] : [];
const intersects = (a, b) => a.some((value) => b.includes(value));
const genders = (item) => values(item.genders).filter((value) => ['mädchen', 'jungen'].includes(value));

// Stable Shopify taxonomy IDs, not translated names. Ancestors cover new subtypes.
// Source: github.com/Shopify/product-taxonomy, data/categories/aa_apparel_accessories.yml.
const TAXONOMY = [
  ['layer', ['aa-1-13-3', 'aa-1-13-12', 'aa-1-13-13', 'aa-1-13-14', 'aa-1-25-9-3', 'aa-1-25-9-11', 'aa-1-25-9-12', 'aa-1-25-9-13']],
  ['bottom', ['aa-1-12', 'aa-1-15', 'aa-1-1-1']],
  ['dress', ['aa-1-4']],
  ['outer', ['aa-1-10']],
  ['night', ['aa-1-17']],
  ['onepiece', ['aa-1-25-10']],
  ['base', ['aa-1-13', 'aa-1-25-9']],
];
// Exact structured type aliases only. This is not an allowlist of recommendable types:
// any new type remains eligible for exact-type recommendations without a code change.
const ALIASES = {
  bottom: ['jeans', 'hose', 'leggings', 'shorts', 'rock'],
  base: ['t-shirt', 'langarmshirt', 'bluse', 'hemd', 'body', 'tops'],
  layer: ['pullover', 'strickjacke', 'hoodie', 'sweatshirt', 'sweatjacke', 'weste'],
  dress: ['kleid'],
  outer: ['jacke', 'regenjacke', 'regenhose', 'regenanzug', 'schneeanzug'],
  night: ['schlafanzug', 'schlafsack', 'bademantel'],
  onepiece: ['overall'],
};
const OUTFITS = {
  bottom: ['base', 'layer'], base: ['bottom', 'layer'], layer: ['base', 'bottom'],
  dress: ['layer'], outer: ['base', 'bottom'], onepiece: ['layer'], night: [],
};

export function group(item) {
  const ids = values(item.categories);
  const taxonomy = TAXONOMY.find(([, categories]) => intersects(categories, ids))?.[0];
  const alias = Object.entries(ALIASES).find(([, types]) => types.includes(normalize(item.type)))?.[0];
  // A broad tops taxonomy still permits a more precise, compatible layer alias.
  return taxonomy === 'base' && alias === 'layer' ? alias : taxonomy || alias || null;
}

export function validSource(source) {
  return Boolean(source?.id && normalize(source.type) && values(source.sizes).length && genders(source).length);
}

const variantSizes = (variant) => {
  const explicit = values(variant?.sizes);
  return explicit.length ? explicit : variant?.sizeOption ? normalize(variant.sizeOption).split(/[\/;]/).map((v) => v.trim()) : [];
};

export function sourceForVariant(product, variantId) {
  if (!product || !Array.isArray(product.variants) || product.variants.length <= 1) return product;
  const selected = product.variants.find((variant) => String(variant.id) === String(variantId));
  const sizes = variantSizes(selected);
  return { ...product, sizes: Array.isArray(product.sizes) ? product.sizes.filter((size) => sizes.includes(normalize(size))) : [] };
}

export function eligible(source, candidate) {
  if (!validSource(source) || !validSource(candidate) || String(source.id) === String(candidate.id) || candidate.available !== true) return false;
  const matchingSizes = values(source.sizes).filter((size) => values(candidate.sizes).includes(size));
  if (!matchingSizes.length || !intersects(genders(source), genders(candidate))) return false;
  const variants = Array.isArray(candidate.variants) ? candidate.variants : [];
  return variants.some((variant) => {
    if (variant.available !== true || variant.stock !== true) return false;
    // An explicit size option may contain a double size, never an inferred range.
    const sizes = variantSizes(variant);
    if (sizes.length) return intersects(matchingSizes, sizes);
    // A single physical item can use its product-level size list. Ambiguous
    // multi-variant products fail closed instead of using another size's stock.
    return variants.length === 1;
  });
}

export function hash(value) {
  let result = 2166136261;
  for (const character of String(value)) result = Math.imul(result ^ character.charCodeAt(0), 16777619);
  return result >>> 0;
}

export function select(source, candidates) {
  if (!validSource(source)) return [];
  const sourceGroup = group(source);
  const unique = new Map();
  for (const candidate of candidates) if (eligible(source, candidate)) unique.set(String(candidate.id), candidate);
  const rank = (a, b) => hash(`${source.id}:${a.id}`) - hash(`${source.id}:${b.id}`) || String(a.id).localeCompare(String(b.id));
  const similar = [...unique.values()].filter((candidate) =>
    normalize(candidate.type) === normalize(source.type) || (sourceGroup && group(candidate) === sourceGroup)
  ).sort((a, b) => {
    const exact = (item) => Number(normalize(item.type) === normalize(source.type));
    return exact(b) - exact(a) || rank(a, b);
  });
  const outfit = [...unique.values()].filter((candidate) =>
    normalize(candidate.type) !== normalize(source.type) && OUTFITS[sourceGroup]?.includes(group(candidate))
  ).sort(rank);
  const selected = similar.slice(0, 2);
  const usedGroups = new Set();
  // Prefer a base top AND a layer, not two t-shirts, when both exist.
  for (const candidate of outfit) {
    if (usedGroups.has(group(candidate))) continue;
    selected.push(candidate);
    usedGroups.add(group(candidate));
    if (usedGroups.size === 2) break;
  }
  for (const candidate of [...outfit, ...similar]) {
    if (selected.length >= 4) break;
    if (!selected.some((item) => String(item.id) === String(candidate.id))) selected.push(candidate);
  }
  return selected.slice(0, 4);
}

export function candidateURL(base, source, kind, page, locationURL) {
  const url = new URL(base, locationURL);
  if (url.origin !== new URL(locationURL).origin) throw new Error('Candidate source must use the current market');
  url.search = '';
  url.searchParams.set('section_id', HELPER);
  // Shopify splits raw comma lists before URL decoding. URLSearchParams encodes
  // commas as %2C, turning a joined list into ONE nonexistent filter value.
  // Repeated parameters preserve each exact value, including multi-size items.
  for (const size of source.sizes) url.searchParams.append(SIZE, size);
  for (const gender of source.genders.filter((v) => genders(source).includes(normalize(v)))) {
    url.searchParams.append(GENDER, gender);
  }
  if (kind === 'exact') url.searchParams.set(TYPE, source.type);
  url.searchParams.set('filter.v.availability', '1');
  url.searchParams.set('sort_by', 'created-descending');
  url.searchParams.set('page', String(page));
  // Retain explicit Draft preview context when present; otherwise Shopify's
  // preview session cookie applies. Never switch domains or countries.
  const preview = new URL(locationURL).searchParams.get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  return url;
}

export function parsePage(html, url, parser = new DOMParser()) {
  const document = parser.parseFromString(html, 'text/html');
  const root = document.querySelector('[data-orikiddo-candidates]');
  if (!root) throw new Error('Candidate section is missing');
  const applied = [...root.querySelectorAll('[data-filter]')];
  // If a filter was disabled or changed in Search & Discovery, never silently
  // scan unfiltered catalogue pages. Even empty results must confirm filters.
  for (const key of [SIZE, GENDER, ...(url.searchParams.has(TYPE) ? [TYPE] : [])]) {
    const expected = url.searchParams.getAll(key).map(normalize);
    if (!expected.every((value) => applied.some((entry) => entry.dataset.filter === key && normalize(entry.dataset.value) === value))) {
      throw new Error(`Required filter is not active: ${key}`);
    }
  }
  return {
    pages: Math.max(1, Number(root.dataset.pages) || 1),
    items: [...root.querySelectorAll('template[data-candidate]')].map((template) => ({
      ...JSON.parse(template.dataset.candidate), template,
    })),
  };
}

export async function collect(source, getPage, budget = 4) {
  if (!validSource(source)) return { items: [], requests: [] };
  const candidates = [];
  const requests = [];
  const pools = [{ kind: 'exact', pages: 1, visited: new Set() }];
  if (group(source)) pools.push({ kind: 'broad', pages: 1, visited: new Set() });
  const limit = Math.min(6, Math.max(2, Number(budget) || 4));
  async function read(pool, page) {
    if (requests.length >= limit || pool.visited.has(page)) return;
    pool.visited.add(page);
    requests.push({ kind: pool.kind, page });
    const result = await getPage(pool.kind, page);
    pool.pages = result.pages;
    candidates.push(...result.items);
  }
  // Sequential: never more than one request in flight, even for sparse sizes.
  for (const pool of pools) await read(pool, 1);
  // Diversify the candidate window too, rather than only shuffling page one.
  for (const pool of [...pools].reverse()) {
    if (pool.pages > 1) await read(pool, 2 + hash(`${source.id}:${pool.kind}`) % (pool.pages - 1));
  }
  while (requests.length < limit) {
    const selection = select(source, candidates);
    const similarCount = selection.filter((item) => normalize(item.type) === normalize(source.type) || (group(source) && group(item) === group(source))).length;
    if (selection.length === 4 && (!OUTFITS[group(source)]?.length || similarCount === 2)) break;
    const options = similarCount < 2 ? pools : [...pools].reverse();
    const pool = options.find((entry) => entry.visited.size < entry.pages);
    if (!pool) break;
    let page = 1;
    while (pool.visited.has(page)) page++;
    await read(pool, page);
  }
  return { items: select(source, candidates), requests };
}

const ElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
export class OrikiddoRecommendations extends ElementBase {
  connectedCallback() {
    try { this.productSource = JSON.parse(this.dataset.source); } catch { this.productSource = null; }
    this.source = sourceForVariant(this.productSource, this.productSource?.selectedVariantId);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const main = document.querySelector(`product-info[data-product-id="${this.productSource?.id}"]`);
        if (!main || main.dataset.section !== data.sectionId) return;
        this.source = sourceForVariant(this.productSource, data.variant?.id);
        this.restart();
      });
    }
    this.onVisibility = () => {
      if (document.visibilityState === 'visible' && this.loadedAt && Date.now() - this.loadedAt > 60000) {
        this.restart();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.unsubscribeVariant?.();
  }

  blocked() {
    return document.querySelector('orikiddo-recommendations') !== this || Boolean(document.querySelector(
      '.related-products-lazy-wrapper, product-recommendations.related-products'
    ));
  }

  clear() {
    this.placeholder(false);
    this.querySelector('[data-results]').hidden = true;
    this.querySelector('ul').replaceChildren();
  }

  placeholder(show) {
    const placeholder = this.querySelector('[data-placeholder]');
    if (placeholder) placeholder.hidden = !show;
    this.setAttribute('aria-busy', String(show));
  }

  showWrapper(show) {
    const wrapper = this.closest('.shopify-section');
    if (wrapper) wrapper.hidden = !show && this.dataset.designMode !== 'true';
  }

  async restart() {
    const revision = (this.revision || 0) + 1;
    this.revision = revision;
    this.controller?.abort();
    await this.task;
    if (!this.isConnected || this.revision !== revision) return;
    this.clear();
    this.loadedAt = 0;
    this.sync();
  }

  start() {
    this.task = this.load();
  }

  sync() {
    if (!this.isConnected) return;
    this.observer?.disconnect();
    const note = this.querySelector('[data-editor-note]');
    const blocked = this.blocked();
    const valid = validSource(this.source);
    if (blocked || !valid) {
      this.controller?.abort();
      this.clear();
      this.showWrapper(false);
      if (note) {
        note.textContent = blocked ? 'Bitte zuerst die alte oder doppelte Empfehlungssektion ausblenden.' : 'Für dieses Produkt fehlen Größe, Zielgruppe oder Produkttyp.';
        note.hidden = false;
      }
      return;
    }
    if (note) note.hidden = true;
    this.showWrapper(true);
    if (this.querySelector('[data-results]').hidden === false) return;
    this.placeholder(true);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.start();
        }
      }, { rootMargin: '300px 0px' });
      this.observer.observe(this);
    } else this.start();
  }

  async load() {
    if (this.loading || this.blocked() || !validSource(this.source)) return;
    this.loading = true;
    const controller = new AbortController();
    this.controller = controller;
    // One deadline covers the entire search, including response bodies.
    const timeout = setTimeout(() => controller.abort(), 10000);
    const started = performance.now();
    const metrics = { requests: 0, bytes: 0, count: 0, reason: 'empty' };
    try {
      const result = await collect(this.source, async (kind, page) => {
        const url = candidateURL(this.dataset.url, this.source, kind, page, location.href);
        metrics.requests++;
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('Candidate request failed or changed market');
        const html = await response.text();
        metrics.bytes += new TextEncoder().encode(html).length;
        if (html.length > 524288) throw new Error('Candidate payload is too large');
        return parsePage(html, url);
      }, this.dataset.maxRequests);
      if (controller.signal.aborted || !this.isConnected || this.blocked()) return;
      const list = this.querySelector('ul');
      list.replaceChildren();
      for (const item of result.items) {
        const li = document.createElement('li');
        li.append(item.template.content.cloneNode(true));
        list.append(li);
      }
      this.placeholder(false);
      this.querySelector('[data-results]').hidden = result.items.length === 0;
      this.showWrapper(result.items.length > 0);
      metrics.count = result.items.length;
      metrics.reason = metrics.count ? 'loaded' : 'empty';
      this.loadedAt = Date.now();
    } catch {
      this.clear();
      this.showWrapper(false);
      metrics.reason = controller.signal.aborted ? 'aborted' : 'error';
      const note = this.querySelector('[data-editor-note]');
      if (note && this.isConnected) {
        note.textContent = 'Keine Empfehlungen geladen. Kandidaten-Sektion und aktive Filter für Größe, Zielgruppe und Produkttyp prüfen.';
        note.hidden = false;
      }
    } finally {
      clearTimeout(timeout);
      this.loading = false;
      metrics.duration = Math.round(performance.now() - started);
      this.dispatchEvent(new CustomEvent('orikiddo:recommendations', { bubbles: true, detail: metrics }));
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('orikiddo-recommendations')) {
  customElements.define('orikiddo-recommendations', OrikiddoRecommendations);
  for (const event of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
    document.addEventListener(event, () => requestAnimationFrame(() => {
      document.querySelectorAll('orikiddo-recommendations').forEach((section) => section.sync());
    }));
  }
}
